import type { GridColDef, GridFilterItem } from '@mui/x-data-grid-premium';
import { get } from 'lodash-es';

import type { NumberFilterParams } from '~/data/schemas/tableFilters';

/**
 * Basic filtering implementation for a single column.
 * @param value query string to filter by
 * @returns matched rows
 */
export const getApplyQuickFilterFn = (value: string, query: string) => {
  return (params: { row: unknown }) => {
    const { row } = params;
    const rowValue = get(row, query);
    if (!rowValue) return false;
    return get(row, query).toLowerCase().includes(value.toLowerCase());
  };
};

export const gridFilterItemToNumberFilterParams = (items: GridFilterItem[]): NumberFilterParams => {
  if (items.length === 0) {
    return {};
  }

  if (items.some((item) => item.field !== items[0].field)) {
    throw new Error('All filter items must be for the same field');
  }

  return {
    eq: items.find((item) => item.operator === '=')?.value,
    gt: items.find((item) => item.operator === '>')?.value,
    gte: items.find((item) => item.operator === '>=')?.value,
    lt: items.find((item) => item.operator === '<')?.value,
    lte: items.find((item) => item.operator === '<=')?.value,
  };
};

/**
 * Whether a filter item actually narrows the table. Two kinds do not: one whose column or
 * operator no longer exists (MUI's `cleanFilterItem` throws on those rather than skipping),
 * and one left with no value, which MUI skips when filtering. Neither counts towards the
 * Clear Filters button or towards a view being edited — the reader sees every row either way.
 */
const filterItemIsActive = (column: GridColDef | undefined, item: GridFilterItem): boolean => {
  const operator = column?.filterOperators?.find((candidate) => candidate.value === item.operator);
  if (!column || !operator) return false;

  try {
    return typeof operator.getApplyFilterFn(item, column) === 'function';
  } catch {
    // An operator that throws when asked throws again when the grid applies it, which is
    // the page-breaking case this guards against. Dropping it loses nothing that worked.
    return false;
  }
};

const DATE_COLUMN_TYPES = new Set(['date', 'dateTime']);
const DATE_INPUT_TYPES = new Set(['date', 'datetime-local']);

// By the column's type or by the operator's input: Compliance's CAT test columns carry no
// type but filter through MUI's date operators, whose input writes a `Date` all the same.
const takesDate = (column: GridColDef, item: GridFilterItem): boolean => {
  if (column.type && DATE_COLUMN_TYPES.has(column.type)) return true;
  const operator = column.filterOperators?.find((candidate) => candidate.value === item.operator);
  const inputType: unknown = operator?.InputComponentProps?.type;
  return typeof inputType === 'string' && DATE_INPUT_TYPES.has(inputType);
};

/**
 * A filter item as the grid itself would hold it, or `null` where the grid would hold none.
 * A saved view reaches the page through JSON, which turns a date filter's `Date` into a
 * string; MUI's own operator reads a string happily, but a page that formats the value for
 * its query throws on anything but a `Date`, on every load. So a string on a date filter
 * becomes a `Date` again, and one that does not parse is dropped — as MUI's date input
 * drops a date it cannot read.
 */
const reviveFilterItem = (
  column: GridColDef | undefined,
  item: GridFilterItem,
): GridFilterItem | null => {
  if (!column || !takesDate(column, item)) return item;
  if (typeof item.value !== 'string' && !(item.value instanceof Date)) return item;

  const date = new Date(item.value);
  return Number.isNaN(date.getTime()) ? null : { ...item, value: date };
};

/**
 * The items that actually narrow the table, with any date a saved view stored as a string
 * turned back into a `Date`. See `filterItemIsActive` and `reviveFilterItem`.
 */
export const activeFilterItems = (
  columns: Record<string, GridColDef | undefined>,
  items: GridFilterItem[],
): GridFilterItem[] =>
  items.flatMap((item) => {
    const column = columns[item.field];
    const revived = reviveFilterItem(column, item);
    return revived && filterItemIsActive(column, revived) ? [revived] : [];
  });

/** Two sets of filter items as the reader would judge them: ids and order carry no meaning. */
export const sameFilterItems = (a: GridFilterItem[], b: GridFilterItem[]): boolean => {
  const spell = (items: GridFilterItem[]) =>
    items
      .map(({ field, operator, value }) => JSON.stringify({ field, operator, value }))
      .sort()
      .join('|');
  return spell(a) === spell(b);
};
